<script lang="ts">
  import { game } from '$stores/game.svelte';
  import { pair } from '$stores/pair.svelte';
  import { open } from '$crypto/encryption';
  import { onMount } from 'svelte';

  let count = $state(3);
  let waitingForFriend = $state(false);
  let pendingState = $state(false);
  let retrying = $state(false);
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let countTimer: ReturnType<typeof setInterval> | null = null;
  let timeoutTimer: ReturnType<typeof setTimeout> | null = null;

  /** Hard cap on relay polling before we surface the pending state (ms). */
  const RELAY_TIMEOUT_MS = 30_000;
  const POLL_INTERVAL_MS = 1500;

  function startCountdown() {
    waitingForFriend = false;
    pendingState = false;
    countTimer = setInterval(() => {
      count -= 1;
      if (count <= 0) {
        if (countTimer) clearInterval(countTimer);
        game.goto('reveal');
      }
    }, 800);
  }

  function tryDecryptFriendOrder(ct: string | null): string[] | null {
    if (!ct || !pair.pairKey) return null;
    try {
      const plain = open({ ct }, pair.pairKey);
      const data = JSON.parse(plain) as { order?: string[]; handle?: string };
      if (Array.isArray(data.order) && data.order.length === 5) {
        if (data.handle && !game.friendHandle) game.friendHandle = data.handle;
        return data.order;
      }
    } catch {
      /* malformed blob — ignore */
    }
    return null;
  }

  async function pollOnce(): Promise<boolean> {
    if (!pair.isBonded || !game.pulse) return false;
    try {
      const url = `/api/pulse/relay?pairId=${encodeURIComponent(pair.pairId)}&pulseId=${encodeURIComponent(game.pulse.id)}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return false;
      const { a = null, b = null } = (await res.json()) as { a: string | null; b: string | null };
      const friendCt = pair.friendRole === 'a' ? a : b;
      const friendOrder = tryDecryptFriendOrder(friendCt);
      if (friendOrder) {
        game.applyFriendOrder(friendOrder);
        return true;
      }
    } catch {
      /* network error — keep polling */
    }
    return false;
  }

  function cleanup() {
    if (pollTimer) clearTimeout(pollTimer);
    if (countTimer) clearInterval(countTimer);
    if (timeoutTimer) clearTimeout(timeoutTimer);
    pollTimer = countTimer = timeoutTimer = null;
  }

  function enterPendingState() {
    cleanup();
    pendingState = true;
    waitingForFriend = false;
    game.pendingFriend = true;
  }

  /** One-shot retry: poll once more, then return to pending if nothing arrived. */
  async function retry() {
    if (retrying) return;
    retrying = true;
    const got = await pollOnce();
    retrying = false;
    if (got) {
      pendingState = false;
      game.pendingFriend = false;
      startCountdown();
    }
  }

  function backHome() {
    cleanup();
    game.pendingFriend = false;
    game.reset();
  }

  onMount(() => {
    // Defensive: solo never lands on Wait. If it does (deep-link, refresh)
    // jump straight to reveal where the echo branch handles rendering.
    if (game.mode === 'solo') {
      game.goto('reveal');
      return cleanup;
    }

    // Bonded fallback path (relay seal failed in Play.svelte) → pending state
    // immediately, no fake friend.
    if (game.pendingFriend) {
      enterPendingState();
      return cleanup;
    }

    // Bonded happy path: friend ct already in store (rare race) OR begin polling.
    if (!pair.isBonded || game.friendOrder.length === 5) {
      startCountdown();
      return cleanup;
    }

    waitingForFriend = true;

    const tick = async () => {
      const got = await pollOnce();
      if (got) {
        startCountdown();
        return;
      }
      pollTimer = setTimeout(tick, POLL_INTERVAL_MS);
    };
    pollTimer = setTimeout(tick, POLL_INTERVAL_MS);

    // No friend in 30 s → pending state. We never fabricate a friend ranking.
    timeoutTimer = setTimeout(() => {
      if (game.friendOrder.length !== 5) enterPendingState();
    }, RELAY_TIMEOUT_MS);

    return cleanup;
  });
</script>

<section class="wait" class:pending={pendingState}>
  {#if pendingState}
    <div class="pending-card">
      <div class="pending-icon" aria-hidden="true">⌁</div>
      <div class="pending-title">Your twin hasn’t pulsed yet</div>
      <div class="pending-sub">
        Share your invite, or come back when {game.friendHandle || 'they'} are ready.
      </div>
      <div class="pending-actions">
        <button class="btn btn-hero" onclick={retry} disabled={retrying}>
          {retrying ? 'Checking…' : 'Retry'}
        </button>
        <button class="btn btn-secondary" onclick={backHome}>Back to home</button>
      </div>
    </div>
  {:else}
    <div class="pulse-ring"><div class="pulse-core"></div></div>
    {#if waitingForFriend}
      <div class="wait-text">Waiting for {game.friendHandle}…</div>
      <div class="wait-sub">Encrypted blob exchange in progress</div>
    {:else}
      <div class="wait-text">
        {count > 1 ? `${game.friendHandle} is locking in…` : 'Twins forming…'}
      </div>
      <div class="wait-sub">Both pulses revealed in {count}</div>
    {/if}
  {/if}
</section>

<style>
  .wait {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px var(--gutter);
  }
  .pulse-ring {
    width: 140px;
    height: 140px;
    border-radius: 50%;
    border: 2px solid var(--primary);
    position: relative;
    display: grid;
    place-items: center;
    margin-bottom: 24px;
    animation: pulse 1.4s ease-out infinite;
  }
  :global(body.brutal) .pulse-ring {
    border-radius: 0;
    border-width: 4px;
    animation: none;
  }
  .pulse-ring::before,
  .pulse-ring::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: 2px solid var(--primary);
    animation: pulse 1.4s ease-out infinite;
  }
  .pulse-ring::after {
    animation-delay: 0.5s;
  }
  :global(body.brutal) .pulse-ring::before,
  :global(body.brutal) .pulse-ring::after {
    display: none;
  }
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(1.6);
      opacity: 0;
    }
  }
  .pulse-core {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: var(--primary);
    box-shadow: var(--shadow-glow);
  }
  :global(body.brutal) .pulse-core {
    border-radius: 0;
    background: var(--text);
    box-shadow: none;
  }
  .wait-text {
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 6px;
  }
  .wait-sub {
    text-align: center;
    font-size: 12px;
    color: var(--text-3);
  }

  /* ============ Pending (relay timeout) ============ */
  .pending-card {
    width: 100%;
    max-width: 360px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 20px 18px 18px;
    background: rgba(0, 0, 0, 0.55);
    border: 2px solid rgba(255, 56, 188, 0.5);
    border-radius: var(--r-md);
    box-shadow: var(--depth-sm) #6d2380, var(--glow-hot);
  }
  :global(body.brutal) .pending-card {
    background: var(--bg);
    border-color: var(--text);
    box-shadow: 3px 3px 0 0 var(--text);
  }
  .pending-icon {
    font-size: 36px;
    line-height: 1;
    color: var(--hot);
    text-shadow: 0 0 18px rgba(255, 56, 188, 0.6);
  }
  :global(body.brutal) .pending-icon {
    color: var(--text);
    text-shadow: none;
  }
  .pending-title {
    font-size: 16px;
    font-weight: 900;
    text-align: center;
    color: var(--text);
    letter-spacing: 0.02em;
  }
  .pending-sub {
    font-size: 12px;
    text-align: center;
    color: var(--text-2);
    line-height: 1.4;
  }
  .pending-actions {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 6px;
  }
  .pending-actions .btn {
    width: 100%;
  }
</style>
